import React, { useState, useEffect } from 'react';
import {
  Lightbulb,
  Target,
  Users,
  Calendar,
  MapPin,
  Search,
  Plus,
  TrendingUp,
  Clock,
  CheckCircle2,
  Loader2,
  AlertCircle
} from 'lucide-react';
import {
  projectService,
  projectCategoryService,
  projectStatsService,
  CommunityProjectWithRelations,
  ProjectCategory
} from '../../services/supabaseProjects';
import { CommunityProject } from '../../types/project.types';
import ProjectDetails from './ProjectDetails';
import CompactProjectCard from './CompactProjectCard';

interface ProjectsHubProps {
  memberId: string;
}

const ProjectsHub: React.FC<ProjectsHubProps> = ({ memberId }) => {
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [projects, setProjects] = useState<CommunityProjectWithRelations[]>([]);
  const [completedProjects, setCompletedProjects] = useState<CommunityProjectWithRelations[]>([]);
  const [categories, setCategories] = useState<(ProjectCategory & { project_count: number })[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [stats, setStats] = useState({ totalProjects: 0, totalFundsRaised: 0, totalParticipants: 0, completedProjects: 0 });
  const [selectedProject, setSelectedProject] = useState<CommunityProject | null>(null);
  const [isProjectDetailsOpen, setIsProjectDetailsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les donnees
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [
        { projects: activeProjects },
        completed,
        categoriesData,
        provincesData,
        globalStats
      ] = await Promise.all([
        projectService.getActive(),
        projectService.getCompleted(),
        projectCategoryService.getWithCount(),
        projectService.getProvinces(),
        projectStatsService.getGlobalStats()
      ]);

      setProjects(activeProjects);
      setCompletedProjects(completed);
      setCategories(categoriesData);
      setProvinces(provincesData);
      setStats(globalStats);
    } catch (err) {
      console.error('Erreur chargement projets:', err);
      setError('Impossible de charger les projets. Veuillez reessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // Adapter les projets pour le composant CompactProjectCard
  const adaptProject = (project: CommunityProjectWithRelations) => {
    const targetAmount = project.funding_goal || project.budget_requested || 0;
    const progress = targetAmount > 0 ? Math.round((project.budget_raised / targetAmount) * 100) : 0;
    const category = project.category as ProjectCategory | undefined;

    return {
      id: project.id,
      title: project.title,
      description: project.description.substring(0, 200) + '...',
      category: category?.name || 'Autre',
      location: `${project.location_city || ''}, ${project.location_province || ''}`.replace(/^, |, $/g, ''),
      targetAmount,
      currentAmount: project.budget_raised,
      supporters: project.participants_count || 0,
      deadline: project.end_date || '',
      status: project.status === 'completed' ? 'completed' : project.status === 'fundraising' ? 'fundraising' : 'active',
      progress,
      featured: project.featured,
      originalProject: project
    };
  };

  const handleProjectClick = (adaptedProject: any) => {
    const original = adaptedProject.originalProject || projects.find(p => p.id === adaptedProject.id) || completedProjects.find(p => p.id === adaptedProject.id);
    if (original) {
      // Cast to CommunityProject for ProjectDetails component
      setSelectedProject(original as CommunityProject);
      setIsProjectDetailsOpen(true);
    }
  };

  // Filtrer les projets
  const getFilteredProjects = () => {
    const sourceProjects = activeTab === 'completed' ? completedProjects : projects;

    return sourceProjects
      .filter(project => {
        const matchesSearch =
          !searchQuery ||
          project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (project.location_city || '').toLowerCase().includes(searchQuery.toLowerCase());

        const matchesProvince =
          !selectedProvince ||
          project.location_province === selectedProvince;

        return matchesSearch && matchesProvince;
      })
      .map(adaptProject);
  };

  const filteredProjects = getFilteredProjects();
  const totalFundsRaised = stats.totalFundsRaised;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-blue animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Erreur</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={loadData} className="btn-udb-primary">
            Reessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header Section - Style UDB */}
      <section className="relative bg-primary-blue text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-float-up"
                style={{
                  width: Math.random() * 3 + 1 + 'px',
                  height: Math.random() * 3 + 1 + 'px',
                  left: Math.random() * 100 + '%',
                  bottom: '-20px',
                  backgroundColor: i % 3 === 0 ? '#ffd400' : i % 3 === 1 ? '#ffffff' : '#00469b',
                  opacity: Math.random() * 0.6 + 0.2,
                  animationDuration: `${Math.random() * 15 + 10}s`,
                  animationDelay: `${Math.random() * 10}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-light text-white">
                Batir ensemble
                <span className="text-primary-yellow font-normal ml-2">nos communautes</span>
              </h1>
              <p className="text-white/80 mt-1 font-light">
                Financement participatif de projets a fort impact social
              </p>
            </div>

            <div className="hidden md:flex items-center gap-6">
              <div className="text-right">
                <div className="text-2xl font-light text-primary-yellow">
                  {stats.totalProjects}
                </div>
                <div className="text-white/60 text-sm">Projets actifs</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-light text-primary-yellow">
                  {Math.round(totalFundsRaised / 1000000)}M
                </div>
                <div className="text-white/60 text-sm">FCFA leves</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation par onglets - Style UDB */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'active'
                  ? 'border-primary-blue text-primary-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Clock size={18} />
              Projets actifs
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'completed'
                  ? 'border-primary-green text-primary-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CheckCircle2 size={18} />
              Projets realises
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'categories'
                  ? 'border-primary-yellow text-primary-yellow'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Target size={18} />
              Categories
            </button>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {(activeTab === 'active' || activeTab === 'completed') && (
          <div>
            {/* Barre de recherche - Style UDB avec card moderne */}
            <div className="card-udb p-8 mb-8">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Rechercher un projet..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-100 focus:border-primary-blue outline-none transition-all bg-white/70"
                  />
                </div>
                <select
                  value={selectedProvince}
                  onChange={(e) => setSelectedProvince(e.target.value)}
                  className="px-4 py-3 border-2 border-gray-100 focus:border-primary-blue outline-none transition-all bg-white/70 text-gray-700"
                >
                  <option value="">Toutes les provinces</option>
                  {provinces.map(province => (
                    <option key={province} value={province}>
                      {province}
                    </option>
                  ))}
                </select>
                {activeTab === 'active' && (
                  <button className="btn-udb-primary flex items-center gap-2">
                    <Plus size={20} />
                    Proposer un projet
                  </button>
                )}
              </div>
            </div>

            {/* Projets vedettes (seulement pour les projets actifs) - Style UDB */}
            {activeTab === 'active' && filteredProjects.filter(p => p.featured).length > 0 && (
              <div className="mb-12">
                <div className="flex items-center space-x-2 mb-8">
                  <TrendingUp className="w-6 h-6 text-primary-yellow" />
                  <span className="text-primary-blue text-sm uppercase tracking-wider font-semibold">Projets vedettes</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8 leading-tight">
                  Projets a fort
                  <span className="block text-primary-green">impact social</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {filteredProjects.filter(p => p.featured).map(project => (
                    <div key={project.id} className="group bg-white/10 backdrop-blur-sm border-2 border-primary-blue/30 p-8 hover:bg-primary-blue/5 hover:border-primary-blue transition-all duration-300 transform hover:scale-105 card-udb">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary-blue transition-colors">{project.title}</h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">{project.description}</p>
                        </div>
                        <div className="bg-primary-yellow/20 text-primary-blue px-3 py-1 text-xs font-medium flex items-center gap-1">
                          <span>*</span>
                          Vedette
                        </div>
                      </div>

                      <div className="space-y-3 mb-6">
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin size={16} className="mr-3 text-primary-green" />
                          <span className="font-medium">{project.location}</span>
                        </div>
                        {project.deadline && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar size={16} className="mr-3 text-primary-blue" />
                            <span>Deadline: {new Date(project.deadline).toLocaleDateString('fr-FR')}</span>
                          </div>
                        )}
                        <div className="flex items-center text-sm text-gray-600">
                          <Users size={16} className="mr-3 text-primary-yellow" />
                          <span>{project.supporters} contributeurs</span>
                        </div>
                      </div>

                      {/* Progress bar - Style UDB */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="font-medium text-gray-700">{project.progress}% finance</span>
                          <span className="text-primary-blue font-semibold">
                            {(project.currentAmount / 1000000).toFixed(1)}M / {((project.targetAmount || 0) / 1000000).toFixed(0)}M FCFA
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 h-2 overflow-hidden">
                          <div
                            className="h-2 bg-primary-green transition-all duration-500"
                            style={{ width: `${Math.min(project.progress, 100)}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => handleProjectClick(project)}
                          className="flex-1 border-2 border-primary-blue text-primary-blue py-3 font-medium hover:bg-primary-blue hover:text-white transition-all"
                        >
                          Voir details
                        </button>
                        <button className="flex-1 btn-udb-secondary">
                          Contribuer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Liste des projets - Style UDB */}
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <Target className="w-5 h-5 text-primary-blue" />
                <span className="text-primary-blue text-sm uppercase tracking-wider font-semibold">
                  {activeTab === 'active' ? 'Projets actifs' : 'Projets realises'}
                </span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-light text-gray-900 mb-8">
                {activeTab === 'active' ? 'Tous les projets' : 'Projets termines'}
                <span className="block text-primary-green font-normal">
                  {activeTab === 'active' ? 'en cours' : 'avec succes'}
                </span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProjects.map(project => (
                  <CompactProjectCard
                    key={project.id}
                    project={project}
                    onViewDetails={handleProjectClick}
                  />
                ))}
              </div>

              {filteredProjects.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-primary-blue/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Lightbulb className="text-primary-blue" size={32} />
                  </div>
                  <h3 className="text-xl font-light text-gray-900 mb-3">Aucun projet trouve</h3>
                  <p className="text-gray-600 font-light">
                    {searchQuery
                      ? `Aucun projet ne correspond a "${searchQuery}"`
                      : "Nouveaux projets bientot disponibles"
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <Target className="w-5 h-5 text-primary-yellow" />
              <span className="text-primary-blue text-sm uppercase tracking-wider font-semibold">Categories</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-light text-gray-900 mb-8">
              Domaines d'action
              <span className="block text-primary-green font-normal">communautaire</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {categories.map(category => (
                <div key={category.id} className="group bg-white/10 backdrop-blur-sm border-2 border-gray-100 p-8 hover:bg-primary-blue/5 hover:border-primary-blue transition-all duration-300 transform hover:scale-105 cursor-pointer card-udb">
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className="w-16 h-16 flex items-center justify-center transition-all group-hover:scale-110"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      <div
                        className="w-8 h-8"
                        style={{ backgroundColor: category.color }}
                      ></div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-light text-primary-blue group-hover:text-primary-green transition-colors">{category.project_count}</div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">projets</div>
                    </div>
                  </div>
                  <h3 className="text-xl font-light text-gray-900 mb-4 group-hover:text-primary-blue transition-colors">{category.name}</h3>
                  <div className="flex items-center text-sm text-primary-blue group-hover:text-primary-green font-medium group-hover:translate-x-2 transition-all">
                    <span>Explorer les projets</span>
                    <span className="ml-2">-&gt;</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal des details du projet */}
      {selectedProject && (
        <ProjectDetails
          project={selectedProject}
          onClose={() => {
            setSelectedProject(null);
            setIsProjectDetailsOpen(false);
          }}
          isOpen={isProjectDetailsOpen}
        />
      )}
    </div>
  );
};

export default ProjectsHub;
